/**
 * 座標の 4 点が合っているかを大きく見る（components/QuadSetupEditor.js の左側いっぱいに重ねる）。
 *
 * 選んでいる枠の 4 点で元の 1 枚（配信の 1 枚か、配信の生の映像）を補正した絵を、補正後の大きさ（pw × ph）の
 * 縦横比のまま領域いっぱいに出し、同梱のプレイマット画像を重ねる。見比べ方は 3 つ:
 *   - 重ねる: プレイマットを半透明に（濃さを変えられる）
 *   - 差分: mix-blend-mode: difference。**ぴったりのところは黒く、ずれたところは線が二重に光る**
 *   - 補正後だけ
 * 見ながら矢印キーで選んでいる角を 1 画素（Shift で 10）、Tab で次の角（QuadSetupEditor の onKeyDown をそのまま使う）。
 * 動かす角は 4 つのボタンか、補正後の絵の四隅の近くを押して選ぶ（補正後の四隅 ＝ 元の 4 点）。今の角は印で出す。
 * ボタンやラジオを押しても矢印キーが効くよう、押したらこの領域にフォーカスを戻す。
 * キーは下の元の 1 枚の領域（QuadSetupEditor の onKeyDown を持つ親）に渡さない（渡すと Tab が 2 回効いて左上 ↔ 右下しか選べなかった）。
 *
 * 補正は WarpCanvas（WebGL で逆の射影変換。CSS の matrix3d は大きい元の 1 枚だと描かれないことがあった）。
 * 配信の映像は QuadSetupEditor の左の canvas（LiveVideo が描く。隠れているだけで描かれ続ける）を毎フレーム元にする。
 */
import { useEffect, useRef, useState } from 'react';
import WarpCanvas from './WarpCanvas';

const CORNERS = ['左上', '右上', '右下', '左下'];

export default function WarpCheck({ points, pw, ph, imgSrc = null, liveCanvasRef = null, playmatSrc = null,
  selected = 0, color = '#4da3ff', onKeyDown, onSelect, onClose }) {
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [mode, setMode] = useState(playmatSrc ? 'diff' : 'image');
  const [opacity, setOpacity] = useState(0.5);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => { const r = el.getBoundingClientRect(); setBox({ w: r.width, h: r.height }); };
    measure();
    el.focus?.();
    // 下の元の 1 枚の拡大（QuadSetupEditor が addEventListener で取っているホイール）に渡さない
    const stopWheel = (e) => e.stopPropagation();
    el.addEventListener('wheel', stopWheel);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => { ro?.disconnect(); el.removeEventListener('wheel', stopWheel); };
  }, []);

  const pad = 24;
  const scale = box.w && box.h ? Math.min((box.w - pad * 2) / pw, (box.h - pad * 2 - 44) / ph) : 0;
  const quad = [[0, 0], [pw, 0], [pw, ph], [0, ph]];
  const corner = quad[selected] ?? [0, 0];
  const refocus = () => boxRef.current?.focus?.();
  const pickNear = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / scale;
    const y = (e.clientY - r.top) / scale;
    const i = quad.map((q, k) => [Math.hypot(q[0] - x, q[1] - y), k]).sort((a, b) => a[0] - b[0])[0][1];
    onSelect?.(i);
    refocus();
  };

  return (
    <div ref={boxRef} tabIndex={0} onKeyDown={(e) => { e.stopPropagation(); onKeyDown?.(e); }} data-testid="warp-check"
      onPointerDown={(e) => e.stopPropagation()} onPointerMove={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
      style={{ position: 'absolute', inset: 0, zIndex: 5, background: '#05070c', outline: 'none', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', fontSize: 13, borderBottom: '1px solid #223' }}>
        <strong style={{ color }}>重ねて確認（補正後 {pw}×{ph}）</strong>
        {playmatSrc ? (
          <>
            <label><input type="radio" checked={mode === 'diff'} onChange={() => { setMode('diff'); refocus(); }} data-testid="warp-check-diff" /> 差分（合うところは黒）</label>
            <label><input type="radio" checked={mode === 'blend'} onChange={() => { setMode('blend'); refocus(); }} /> 重ねる</label>
          </>
        ) : null}
        <label><input type="radio" checked={mode === 'image'} onChange={() => { setMode('image'); refocus(); }} /> 補正後だけ</label>
        {mode === 'blend' ? (
          <label>プレイマットの濃さ <input type="range" min={0} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} onPointerUp={refocus} /></label>
        ) : null}
        <span style={{ display: 'inline-flex', gap: 4 }} data-testid="warp-check-corners">
          {CORNERS.map((c, i) => (
            <button key={c} type="button" onClick={() => { onSelect?.(i); refocus(); }} data-testid={`warp-check-corner-${i}`}
              style={{ padding: '2px 8px', borderRadius: 4, border: `2px solid ${color}`, background: i === selected ? color : 'transparent', color: i === selected ? '#000' : '#e8eefc', fontWeight: i === selected ? 700 : 400 }}>
              {c}
            </button>
          ))}
        </span>
        <span style={{ color: '#8a93a8' }}>矢印キーで選んだ角を 1 画素（Shift で 10）・Tab で次の角・絵の四隅の近くを押しても選べる</span>
        <button type="button" onClick={onClose} data-testid="warp-check-close" style={{ marginLeft: 'auto' }}>閉じる</button>
      </div>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {scale > 0 ? (
          <div data-testid="warp-check-stage" onPointerDown={pickNear} style={{ cursor: 'crosshair', position: 'absolute', left: (box.w - pw * scale) / 2, top: pad, width: pw * scale, height: ph * scale, overflow: 'hidden', background: '#000' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: pw, height: ph, transformOrigin: '0 0', transform: `scale(${scale})`, overflow: 'hidden', isolation: 'isolate' }}>
              <WarpCanvas points={points} pw={pw} ph={ph} imgSrc={liveCanvasRef ? null : imgSrc} sourceCanvasRef={liveCanvasRef}
                data-testid="warp-check-canvas" style={{ position: 'absolute', left: 0, top: 0, width: pw, height: ph }} />
              {playmatSrc && mode !== 'image' ? (
                <img src={playmatSrc} alt="" width={pw} height={ph} data-testid="warp-check-playmat"
                  style={{ position: 'absolute', left: 0, top: 0, opacity: mode === 'diff' ? 1 : opacity, mixBlendMode: mode === 'diff' ? 'difference' : 'normal' }} />
              ) : null}
            </div>
            <div style={{ position: 'absolute', left: corner[0] * scale - 9, top: corner[1] * scale - 9, width: 18, height: 18, borderRadius: 9, border: `3px solid ${color}`, boxShadow: '0 0 0 2px #000', pointerEvents: 'none' }} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
